import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { ArticleSchema } from '@/components/feature/SeoSchema';

export default function BasetaoDeliveryTime() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="min-h-screen">
      <Header />
      <ArticleSchema slug="basetao-delivery-time" />
      <article className="pt-24 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-foreground-900 transition-colors whitespace-nowrap">Home</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-900 font-medium">Delivery Time Guide</li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs font-semibold tracking-wider uppercase text-secondary-700 bg-secondary-100 rounded-full">Delivery</span>
              <span className="text-xs text-foreground-500">Updated August 2026</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-5">
              Basetao Delivery Time Guide 2026 — How Long Does Shipping Take?
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              One of the most common questions from new <strong>Basetao</strong> users is about delivery time. This guide provides realistic estimates for how long Basetao shipping takes to various destinations, factors that affect delivery speed, and practical tips for getting your orders faster.
            </p>
          </header>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Delivery Time by Shipping Method</h2>
              <div className="overflow-x-auto rounded-xl border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left px-4 py-3 font-semibold text-foreground-900">Method</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-900">USA</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-900">UK/Europe</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-900">Australia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Economy Air', '15-30 days', '15-35 days', '15-30 days'],
                      ['EUB/ePacket', '10-20 days', '12-25 days', '12-22 days'],
                      ['EMS', '7-15 days', '7-18 days', '8-16 days'],
                      ['DHL Express', '3-7 days', '3-7 days', '4-8 days'],
                      ['FedEx', '3-7 days', '3-7 days', '4-8 days'],
                    ].map((row, idx) => (
                      <tr key={idx} className="border-t border-background-200/50">
                        {row.map((cell, cIdx) => <td key={cIdx} className="px-4 py-3 text-foreground-600">{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Factors That Affect Delivery Speed</h2>
              <ul className="space-y-3 ml-5">
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Customs clearance:</strong> Some countries have longer customs processing times than others, which can add days or weeks to delivery.</li>
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Peak seasons:</strong> Major shopping events and holidays create shipping backlogs that slow down all carriers.</li>
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Package size and weight:</strong> Larger and heavier packages may require different handling that affects transit times.</li>
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Remote destinations:</strong> Delivery to rural or remote areas typically takes longer than major city destinations.</li>
              </ul>
            </section>

            <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">FAQ</h2>
              <div className="space-y-5">
                {[
                  { q: 'What is the fastest Basetao shipping method?', a: 'DHL Express and FedEx are consistently the fastest, delivering most packages within 3-7 business days to major destinations worldwide.' },
                  { q: 'Why is my package taking longer than the estimate?', a: 'Customs delays, holiday backlogs, and weather events are common causes. Check your tracking information for the most current status.' },
                ].map((faq, idx) => (
                  <div key={idx}><h3 className="text-base font-semibold text-foreground-900 mb-1.5">{faq.q}</h3><p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p></div>
                ))}
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors duration-300 whitespace-nowrap"><i className="ri-home-4-line"></i>Back to Homepage</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-foreground-900 bg-background-100 hover:bg-background-200 border border-background-300/60 rounded-full transition-colors duration-300 whitespace-nowrap">Visit Main Website<i className="ri-external-link-line"></i></a>
            </div>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}